import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Clock, Users, Flame, Dumbbell, Zap, Target, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { ScrollProgressBar } from '@/components/ScrollProgressBar';
import { BOX_IMAGES } from '@/data/images';

const EASE = [0.22, 1, 0.36, 1];

export default function ClassesPage() {
  const classes = [
    {
      slug: 'crossfit-wod',
      title: 'WOD CrossFit',
      subtitle: 'Workout Of the Day',
      desc: "Mouvements fonctionnels variés à haute intensité. Chaque jour un nouveau challenge pour repousser vos limites.",
      duration: '60 min',
      capacity: '12',
      icon: Flame,
      color: 'from-green-700 to-green-500',
      image: BOX_IMAGES.groupBarbell,
    },
    {
      slug: 'hyrox',
      title: 'WOD HYROX',
      subtitle: 'Training Club',
      desc: "Préparation spécifique aux compétitions HYROX : alternance de courses et d'exercices fonctionnels.",
      duration: '60 min',
      capacity: '12',
      icon: Zap,
      color: 'from-orange-600 to-amber-500',
      image: BOX_IMAGES.deadlift,
    },
    {
      slug: 'team-wod',
      title: 'WOD Team CrossFit',
      subtitle: 'Entraînement en équipe',
      desc: "Entraînement en équipe pour renforcer la cohésion et la motivation. Travaillez ensemble, progressez ensemble.",
      duration: '60 min',
      capacity: '12',
      icon: Users,
      color: 'from-blue-600 to-cyan-500',
      image: BOX_IMAGES.kettlebell,
    },
    {
      slug: 'team-hyrox',
      title: 'WOD Team HYROX',
      subtitle: 'Compétition en équipe',
      desc: "Format compétitif en équipe spécial HYROX. Partagez l'effort et la stratégie pour la meilleure performance.",
      duration: '60 min',
      capacity: '12',
      icon: Target,
      color: 'from-purple-600 to-pink-500',
      image: BOX_IMAGES.rower,
    },
    {
      slug: 'free-access',
      title: 'Free Access',
      subtitle: 'Entraînement libre',
      desc: "Créneaux en accès libre pour vous entraîner en autonomie avec tout l'équipement de la box.",
      duration: 'Variable',
      capacity: 'Libre',
      icon: Dumbbell,
      color: 'from-teal-600 to-emerald-500',
      image: BOX_IMAGES.rings,
    },
    {
      slug: 'team-xl',
      title: 'WOD Team XL',
      subtitle: 'Format XXL mensuel',
      desc: "Le grand rendez-vous mensuel : format XXL en équipe jusqu'à 18 athlètes, 1ᵉʳ dimanche du mois. Esprit box à 100%.",
      duration: '75-90 min',
      capacity: '18',
      icon: Users,
      color: 'from-rose-600 to-red-500',
      image: BOX_IMAGES.groupBarbell,
    },
  ];

  return (
    <div className="min-h-screen">
      <PageMeta
        title="Programmes"
        description="Découvrez nos programmes : CrossFit WOD, HYROX, Team WOD, Team HYROX et Free Access. Coaching sportif professionnel à CrossFit Saint-Chamond. Tous niveaux."
        path="/programmes"
      />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'Programmes' }]} />
      <ScrollProgressBar />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/programmes"
        eyebrow="CrossFit & HYROX"
        title="NOS PROGRAMMES"
        highlight="PROGRAMMES"
        subtitle="Des cours encadrés par des coachs certifiés, accessibles à tous les niveaux. Découvrez chaque programme en détail et choisissez celui qui correspond à vos objectifs."
        image={BOX_IMAGES.rings}
        alt="Programmes CrossFit Saint-Chamond — WOD, HYROX, Team WOD, Free Access"
        breadcrumbs={[{ name: 'Accueil', href: '/' }, { name: 'Programmes' }]}
      />

      {/* Classes Grid */}
      <section className="section-padding bg-white" data-testid="classes-grid">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-5%' }}
                  transition={{
                    duration: 0.7,
                    delay: idx * 0.08,
                    ease: EASE,
                  }}
                >
                  <Card
                    data-testid={`class-card-${idx}`}
                    className="group overflow-hidden h-full border-border hover:border-primary/40 transition-all duration-500 hover:shadow-[0_20px_60px_-15px_hsla(155,75%,18%,0.25)] hover:-translate-y-2 rounded-2xl"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img decoding="async" src={item.image} alt={`${item.title} à CrossFit Saint-Chamond`} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className={`absolute top-3 right-3 bg-gradient-to-r ${item.color} text-white px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider shadow-lg`}>
                        {item.subtitle}
                      </div>
                      <div className="absolute bottom-3 left-3">
                        <div className="w-10 h-10 bg-white/15 backdrop-blur-md rounded-xl flex items-center justify-center ring-1 ring-white/20">
                          <Icon className="h-5 w-5 text-white" strokeWidth={2.2} />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <h2 className="font-heading text-xl text-dark mb-2 tracking-wide">{item.title}</h2>
                      <p className="text-muted-foreground text-sm mb-5 leading-relaxed">{item.desc}</p>
                      <div className="flex items-center gap-5 mb-5 pb-5 border-b border-border">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-primary" />
                          <span className="text-xs font-medium">{item.duration}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-primary" />
                          <span className="text-xs font-medium">{item.capacity === 'Libre' ? 'Accès libre' : `${item.capacity} pers.`}</span>
                        </div>
                      </div>
                      <Link to={`/programmes/${item.slug}`}>
                        <Button className="btn-shine w-full bg-dark hover:bg-primary text-white font-heading tracking-wider text-sm py-5 rounded-xl transition-all duration-300 group">
                          EN SAVOIR PLUS
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Button>
                      </Link>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-primary py-20 overflow-hidden" data-testid="classes-cta">
        <div className="absolute inset-0 mesh-gradient opacity-30 pointer-events-none" />
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-heading text-5xl md:text-6xl text-white mb-4 leading-[0.95]">
            PRÊT À <span className="font-display italic text-white/70">COMMENCER</span> ?
          </h2>
          <p className="text-white/80 text-base mb-10 max-w-xl mx-auto">
            Coaching sportif, encadrement professionnel et accessible à tous.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="btn-shine w-full sm:w-auto bg-white hover:bg-white/90 text-primary font-heading tracking-wider px-10 py-7 rounded-xl transition-all duration-300 hover:scale-[1.03] shadow-xl">
                NOUS CONTACTER
              </Button>
            </Link>
            <Link to="/planning">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-primary font-heading tracking-wider px-10 py-7 rounded-xl bg-transparent transition-all duration-300">
                VOIR LE PLANNING
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
